import React, {useState, useEffect} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { Button, TextField, Typography } from '@mui/material';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Modal from '@mui/material/Modal';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { adminGetTheoryQuestion, adminUpdateTheoryQuestion, adminDeleteTheoryQuestion } from "../../api/Api";
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';

interface PropsAdminTheoryQuestions {
    token: string;
}

const fieldSx = (colors: any) => ({
    '& .MuiOutlinedInput-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.9rem',
        color: colors.text.primary,
        '& fieldset': { borderColor: colors.border.default },
        '&:hover fieldset': { borderColor: colors.border.accent },
        '&.Mui-focused fieldset': { borderColor: colors.accent.cyan, boxShadow: `0 0 0 2px ${colors.accent.cyanGlow}` },
    },
    '& .MuiInputLabel-root': {
        fontFamily: '"Share Tech Mono", monospace',
        fontSize: '0.82rem',
        letterSpacing: '0.08em',
        color: colors.text.secondary,
        '&.Mui-focused': { color: colors.accent.cyan },
    },
});

const selectSx = (colors: any) => ({
    fontFamily: '"Share Tech Mono", monospace',
    fontSize: '0.9rem',
    color: colors.text.primary,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent.cyan },
    '& .MuiSvgIcon-root': { color: colors.text.secondary },
});

const AdminUpdateTheoryQuestion = (props: PropsAdminTheoryQuestions) => {
    const colors = useColors();
    const navigate = useNavigate();
    const { id } = useParams();
    const [openDelete, setOpenDelete] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [message, setMessage] = useState("");
    const [stageId, setStageId] = useState<string>("");
    const [orderIndex, setOrderIndex] = useState(0);
    const [question, setQuestion] = useState("");
    const [choiceA, setChoiceA] = useState("");
    const [choiceB, setChoiceB] = useState("");
    const [choiceC, setChoiceC] = useState("");
    const [choiceD, setChoiceD] = useState("");
    const [correctChoice, setCorrectChoice] = useState("a");
    const [explanation, setExplanation] = useState("");

    const token = props.token;

    useEffect(() => {
        const fetchData = async () => {
            const data = await adminGetTheoryQuestion(token, id);
            if (!data) return;
            setStageId(data.stage_id);
            setOrderIndex(data.order_index);
            setQuestion(data.question);
            setChoiceA(data.choices.a);
            setChoiceB(data.choices.b);
            setChoiceC(data.choices.c);
            setChoiceD(data.choices.d);
            setCorrectChoice(data.correct_choice);
            setExplanation(data.explanation || "");
        }
        fetchData();
    }, []);

    const handleUpdate = async (e: any) => {
        e.preventDefault();
        setProcessing(true);
        const response = await adminUpdateTheoryQuestion(token, id, {
            order_index: orderIndex,
            question,
            choice_a: choiceA,
            choice_b: choiceB,
            choice_c: choiceC,
            choice_d: choiceD,
            correct_choice: correctChoice,
            explanation,
        });
        setProcessing(false);
        setMessage(response.msg);
    };

    const handleDelete = async (e: any) => {
        e.preventDefault();
        await adminDeleteTheoryQuestion(token, id);
        navigate(`/admin/theory/stage/${stageId}/questions`);
    };

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: 'Theory Stages', to: '/admin/theory/stages' },
                    { label: 'Questions', to: stageId ? `/admin/theory/stage/${stageId}/questions` : '/admin/theory/stages' },
                    { label: 'Edit' },
                ]}
                mb="32px"
            />

            <GlowCard accentTop glowOnHover={false} sx={{ maxWidth: '680px' }}>
                <Box sx={{ padding: '32px' }}>
                    <Box component="form" onSubmit={handleUpdate} noValidate>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            <TextField
                                sx={{ ...fieldSx(colors), width: '140px' }}
                                required
                                label="Order"
                                type="number"
                                value={orderIndex}
                                onChange={(e) => setOrderIndex(Number(e.target.value))}
                            />
                            <TextField
                                sx={fieldSx(colors)}
                                required
                                fullWidth
                                multiline
                                minRows={2}
                                label="Question"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                            />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice A" value={choiceA} onChange={(e) => setChoiceA(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice B" value={choiceB} onChange={(e) => setChoiceB(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice C" value={choiceC} onChange={(e) => setChoiceC(e.target.value)} />
                            <TextField sx={fieldSx(colors)} required fullWidth label="Choice D" value={choiceD} onChange={(e) => setChoiceD(e.target.value)} />

                            <FormControl sx={{ width: '160px' }}>
                                <InputLabel sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', color: colors.text.secondary, '&.Mui-focused': { color: colors.accent.cyan } }}>
                                    Correct Choice
                                </InputLabel>
                                <Select
                                    value={correctChoice}
                                    label="Correct Choice"
                                    onChange={(e) => setCorrectChoice(e.target.value)}
                                    sx={selectSx(colors)}
                                    MenuProps={{ PaperProps: { sx: { backgroundColor: colors.bg.surface, color: colors.text.primary } } }}
                                >
                                    {['a', 'b', 'c', 'd'].map((c) => (
                                        <MenuItem key={c} value={c} sx={{ fontFamily: '"Share Tech Mono", monospace' }}>{c.toUpperCase()}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                sx={fieldSx(colors)}
                                fullWidth
                                multiline
                                minRows={2}
                                label="Explanation (optional)"
                                value={explanation}
                                onChange={(e) => setExplanation(e.target.value)}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="outlined"
                                disabled={processing}
                                startIcon={processing ? <CircularProgress size={16} sx={{ color: colors.accent.cyan }} /> : <SaveIcon />}
                                sx={{
                                    borderColor: colors.accent.cyan,
                                    color: colors.accent.cyan,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                }}
                            >
                                {processing ? 'Saving...' : 'Save Changes'}
                            </Button>

                            <Box sx={{ height: '1px', backgroundColor: colors.border.subtle, margin: '4px 0' }} />

                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setOpenDelete(true)}
                                sx={{
                                    borderColor: colors.accent.red,
                                    color: colors.accent.red,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontWeight: 700,
                                    fontSize: '0.8rem',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    padding: '12px',
                                    borderRadius: '6px',
                                }}
                            >
                                Delete Question
                            </Button>

                            {message && (
                                <Box sx={{
                                    padding: '12px 16px',
                                    borderRadius: '6px',
                                    backgroundColor: colors.accent.cyanGlow,
                                    border: `1px solid ${colors.accent.cyan}44`,
                                    fontFamily: '"Share Tech Mono", monospace',
                                    fontSize: '0.8rem',
                                    color: colors.accent.cyan,
                                }}>
                                    {message}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Box>
            </GlowCard>

            <Modal open={openDelete} onClose={() => setOpenDelete(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '400px' },
                    backgroundColor: colors.bg.secondary,
                    border: `1px solid ${colors.accent.red}55`,
                    borderRadius: '8px',
                    padding: '32px',
                    boxShadow: `0 0 40px ${colors.accent.red}22`,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <WarningAmberIcon sx={{ color: colors.accent.red, fontSize: '1.5rem' }} />
                        <Typography sx={{ fontFamily: '"Orbitron", sans-serif', fontWeight: 700, fontSize: '1.1rem', color: colors.text.primary }}>
                            Delete Question
                        </Typography>
                    </Box>
                    <Typography sx={{ fontFamily: '"Share Tech Mono", monospace', fontSize: '0.82rem', color: colors.text.secondary, marginBottom: '24px', lineHeight: 1.6 }}>
                        This removes the question permanently and shifts every content block referencing a positional
                        index after it in the corresponding stage file. This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '12px' }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => setOpenDelete(false)}
                            sx={{ borderColor: colors.border.default, color: colors.text.secondary, fontFamily: '"Share Tech Mono", monospace', fontWeight: 600, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={handleDelete}
                            startIcon={<DeleteOutlineIcon />}
                            sx={{ borderColor: colors.accent.red, color: colors.accent.red, fontFamily: '"Share Tech Mono", monospace', fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.1em', textTransform: 'uppercase' }}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </PageContainer>
    );
};

export default AdminUpdateTheoryQuestion;
